import { CalendarDay, CalendarMonth, CalendarSeries, Dates, Objects } from "@salaxy/core";
import { ReportOptions, ReportLayout } from "../../model";

/**
 * Represents the result of a calendar mapping operation.
 */
export type CalendarResult = {
  /** An array of mapped calendar months. */
  months?: MappendCalendarMonth[]
}

/**
 * Represents a mapped calendar month.
 */
export type MappendCalendarMonth = {
  /** The title of the calendar month. */
  title: string,
  /** An array of mapped calendar days. */
  days: ExtendedCalendarDay[],
  /** An array of calendar series. */
  series: ExtendedCalendarSeries[]
  /** An array of weeknumbers in month. */
  weeks: {weekNumber: number, colspan: number}[]
};

/**
 * Represents a mapped calendar day with an additional background color property.
 */
export type ExtendedCalendarDay = CalendarDay & {style?: string, hasEvents?: boolean };

/**
 * Represents an extended calendar series which includes a calendar day and an array of extended calendar days.
 */
export type ExtendedCalendarSeries = CalendarSeries & {days: ExtendedCalendarDay[] };

/**
 * The CalendarMapper class provides methods to generate and manipulate calendar data,
 * including mapping months and series to a structured result, and loading report layouts
 * and options for invoice reports.
 */
export class CalendarMapper {

  /**
   * Generates calendar data by mapping months and series to a structured result.
   * @param {CalendarMonth[]} months - An array of calendar months to be processed.
   * @param {CalendarSeries[]} series - An array of calendar series to be mapped to each month.
   * @returns {CalendarResult} The structured calendar result containing mapped months with their respective days, series, title, and weeks.
   */
  public static getCalendarData(months: CalendarMonth[], series: CalendarSeries[]): CalendarResult {
    const result: CalendarResult = {months: []};
    months.forEach((month) => {
      const mappedMonth: MappendCalendarMonth = {days: undefined, series: undefined, title: undefined, weeks: undefined};
      mappedMonth.title = `${month.title} ${month.year}`;
      mappedMonth.days = month.days;
      mappedMonth.series = series.map((s, i) => {
        const seriesResult: ExtendedCalendarSeries =  {
          ...s,
          days: Objects.copy(month.days)
        };
        seriesResult.days.forEach(day => {
          day.hasEvents = false;
          if(day.periods[i].length > 0) {
            day.hasEvents = true;
          }
        });
        return seriesResult;
      });
      mappedMonth.weeks = this.getWeekNumbersForMonth(month.days);
      result.months.push(mappedMonth);
    });
    return result;
  }

  /**
   * Loads the invoice layout for pdf or html reports.
   * @param pdf Are we rendering a pdf report or not.
   * @returns Returns invoice layout.
   */
  public static loadReportLayout(pdf: boolean): ReportLayout {
    const reportLayout: ReportLayout = {
      isLayoutA4Portrait: false,
      isLayoutA4Landscape: true,
      layout: "a4Landscape",
      date: Dates.getFormattedDate(Dates.getToday()),
      isPreview: false,
      mode: pdf ? "pdf" : "htmlPage",
      isModeHtmlPage: !pdf,
      isModePdf: pdf,
    };
    return reportLayout;
  }

  /**
   * Loads Invoice specific ReportOptions
   * @returns ReportOptions for report
   */
  public static loadReportOptions(): ReportOptions {
    return {
      css: "",
      printBackground: true,
      margin: {
        top: 30,
        bottom: 20,
        left: 10,
        right: 10,
      },
      headerTemplate: null,
      footerTemplate: null,
      logo: ""
    };
  }

  private static getWeekNumbersForMonth(monthDays: CalendarDay[]): {weekNumber: number, colspan: number}[] {
    const result: {weekNumber: number, colspan: number}[] = [];
    const getWeek = (weekNumber: number) => {
      const foundWeek = result.find(x => x.weekNumber === weekNumber);
      if(foundWeek) {
        return foundWeek;
      }

      const week = {weekNumber: weekNumber, colspan: 0};
      result.push(week);
      return week;
    };
    monthDays.forEach(day => {
      const weekNumber = Dates.getWeekNumber(day.date);
      ++getWeek(weekNumber).colspan;
    });

    return result;
  }
}